import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import GoogleOAuthButton from "@/components/GoogleOAuthButton";

export default function Login() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/";
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        const res = await login(email, password);
        setSubmitting(false);
        if (res.ok) navigate(next);
        else setError(res.error);
    };

    return (
        <div className="max-w-md mx-auto px-6 py-16">
            <div className="text-[#ff4500] text-xs uppercase tracking-[0.3em] mb-2">Welcome back</div>
            <h1 className="font-display text-5xl tracking-wide">Log in</h1>
            <p className="text-sm text-[#a0a0a0] mt-2">
                New here?{" "}
                <Link to="/register" className="text-[#ff4500] hover:underline">Create an account</Link>
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
                <GoogleOAuthButton label="Continue with Google" />
                <div className="flex items-center gap-3 py-1">
                    <div className="h-px flex-1 bg-[#2a2a2a]" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#666]">or with email</span>
                    <div className="h-px flex-1 bg-[#2a2a2a]" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Email</Label>
                    <Input
                        data-testid="login-email"
                        type="email"
                        name="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2 h-12 rounded-none bg-[#141414] border-[#2a2a2a]"
                    />
                </div>
                <div>
                    <div className="flex items-center justify-between">
                        <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Password</Label>
                        <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[10px] uppercase tracking-[0.15em] text-[#a0a0a0] hover:text-[#ff4500]">Forgot?</Link>
                    </div>
                    <div className="relative mt-2">
                        <Input
                            data-testid="login-password"
                            type={showPassword ? "text" : "password"}
                            name="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="h-12 rounded-none bg-[#141414] border-[#2a2a2a] pr-12"
                        />
                        <button
                            type="button"
                            data-testid="toggle-password-visibility"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            title={showPassword ? "Hide password" : "Show password"}
                            className="absolute inset-y-0 right-0 flex items-center px-3 text-[#a0a0a0] hover:text-[#ff4500] transition-colors"
                        >
                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                {error && <div data-testid="login-error" className="text-sm text-[#ff4500] border border-[#ff4500] p-3">{error}</div>}

                <Button
                    type="submit"
                    data-testid="login-submit"
                    disabled={submitting}
                    className="w-full h-12 rounded-none bg-[#ff4500] hover:bg-[#ff5722] text-white uppercase tracking-[0.2em] text-xs font-semibold"
                >
                    {submitting ? "Signing in…" : "Log in"}
                </Button>
            </form>
        </div>
    );
}
